<template>
    <div>
        <b-breadcrumb class="px-4 bg-light pb-0" :items="breadcrumbs" />
        <div class="page-content">
            <div
                class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mt-4 mb-3"
            >
                <div class="title">
                    <h1 class="mb-0">Notification Settings</h1>
                </div>
                <div>
                    <div v-if="isSaving" class="updated">Saving...</div>
                    <div v-else-if="lastUpdatedDurationText" class="updated">
                        Last updated {{ lastUpdatedDurationText }} ago
                    </div>
                </div>
            </div>
            <b-card footer-class="p-0" class="side-tab">
                <div class="tab-body row">
                    <!-- side tabs on desktop view -->
                    <div class="col-md-2 tabs">
                        <div v-if="!isLoading" class="nav-scrollable">
                            <div
                                class="text"
                                v-for="(type, index) in notificationTypes"
                                @click="switchNotificationType(type)"
                                :key="index"
                                :id="'nav-' + type.type"
                                :class="{ active: selectedType.id == type.id }"
                            >
                                {{ type.label }}
                            </div>
                        </div>
                        <div v-else><loader-list /></div>
                    </div>
                    <div id="tab-details" class="col-md-10">
                        <div v-if="isLoading">
                            <loader-custom :rowCount="5" />
                        </div>
                        <div v-else>
                            <div class="d-flex">
                                <div class="my-2">
                                    <h2 class="mb-0 title p-0">
                                        {{ selectedType.label }}
                                    </h2>
                                    <p
                                        v-if="selectedType.description"
                                        class="mb-0 mt-2"
                                    >
                                        {{ selectedType.description }}
                                    </p>
                                </div>
                            </div>
                            <div
                                class="mt-3"
                                v-show="selectedType.type !== 'TASK'"
                            >
                                <toggle-input
                                    id="notification_all"
                                    v-model="data.allow_notification"
                                    label="Allow Notifications"
                                    :disabled="disableToggleNotification"
                                    @change="updateAll"
                                />
                            </div>
                            <div class="p-4 bg-light rounded mt-4">
                                <div class="d-flex">
                                    <div
                                        class="flex-fill d-flex align-items-center"
                                    >
                                        <toggle-input
                                            id="notification_banner"
                                            v-model="data.show_banner"
                                            label="Banners"
                                            :disabled="!data.allow_notification"
                                            @change="updateBadgeBanner"
                                        />
                                        <i
                                            class="cus-information ml-2"
                                            id="tooltip-banner"
                                        ></i>
                                        <b-tooltip
                                            target="tooltip-banner"
                                            custom-class="banner-tooltip"
                                        >
                                            <img
                                                src="assets/banner_sample.png"
                                                height="50"
                                            />
                                        </b-tooltip>
                                    </div>
                                    <div
                                        class="flex-fill d-flex align-items-center"
                                    >
                                        <toggle-input
                                            id="notification_badge"
                                            v-model="data.show_badge"
                                            label="Badges"
                                            :disabled="
                                                !data.allow_notification ||
                                                disableToggleBadge
                                            "
                                            @change="updateBadgeBanner"
                                        />
                                        <i
                                            class="cus-information ml-2"
                                            id="tooltip-badge"
                                        ></i>
                                        <b-tooltip target="tooltip-badge">
                                            <img
                                                src="assets/badge_sample.png"
                                                height="40"
                                            />
                                        </b-tooltip>
                                    </div>
                                </div>
                                <div
                                    v-if="displayProvider || displayUnassigned"
                                    class="mt-4"
                                >
                                    <label>Show records from</label>
                                    <div
                                        v-if="displayUnassigned"
                                        class="mt-2"
                                        :class="!isProvider ? 'mb-2' : 'mb-4'"
                                    >
                                        <toggle-input
                                            id="notification_unassigned"
                                            v-model="data.unassigned"
                                            label="Unassigned"
                                            :disabled="
                                                !data.allow_notification ||
                                                disableToggleBadge
                                            "
                                            @change="save"
                                        />
                                    </div>
                                    <div
                                        v-if="isProvider"
                                        class="btn-group d-block"
                                    >
                                        <button
                                            type="button"
                                            class="btn"
                                            :class="{
                                                'btn-success': myRecordOnly,
                                                'btn-secondary': !myRecordOnly
                                            }"
                                            @click="
                                                () =>
                                                    updateProviderToggle(
                                                        'own_record'
                                                    )
                                            "
                                        >
                                            My Records Only
                                        </button>
                                        <button
                                            type="button"
                                            class="btn"
                                            :class="{
                                                'btn-success': !myRecordOnly,
                                                'btn-secondary': myRecordOnly
                                            }"
                                            @click="
                                                () =>
                                                    updateProviderToggle(
                                                        'multiple'
                                                    )
                                            "
                                        >
                                            Multiple Providers
                                        </button>
                                    </div>

                                    <!-- list of providers checkbox here !-->
                                    <div v-show="!myRecordOnly || !isProvider">
                                        <div
                                            :class="
                                                !isProvider ? 'row' : 'row pt-4'
                                            "
                                        >
                                            <div
                                                class="col-sm-12 col-lg-12 py-2"
                                            >
                                                <toggle-input
                                                    :id="'select-all-provider'"
                                                    v-model="data.all_provider"
                                                    label="Select All"
                                                    :disabled="
                                                        !data.allow_notification
                                                    "
                                                    @change="toggleAllProvider"
                                                />
                                            </div>
                                            <div
                                                v-for="provider in providers"
                                                :key="provider.actor_id"
                                                class="col-sm-6 col-lg-3 py-2"
                                            >
                                                <toggle-input
                                                    :id="provider.actor_id"
                                                    v-model="
                                                        provider.isSelected
                                                    "
                                                    :label="
                                                        provider.actor.label
                                                    "
                                                    :disabled="
                                                        !data.allow_notification ||
                                                        disableIndividual(
                                                            provider
                                                        )
                                                    "
                                                    @change="
                                                        updateIndividualProvider
                                                    "
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </b-card>
        </div>
    </div>
</template>
<script>
import StaffService from "@/services/staff";
import NotificationService from "@/services/setting/notification";
import ToggleInput from "@/components/commons/toggle-input.vue";
import LoaderCustom from "@/components/elements/loader-custom";
import LoaderList from "@/components/elements/loader-list";
import SettingMixin from "@/mixins/settings";
import { isEmpty } from "lodash";

export default {
    mixins: [SettingMixin],
    components: { ToggleInput, LoaderCustom, LoaderList },
    data() {
        return {
            isLoading: true,
            isSaving: false,
            notificationTypes: [],
            providers: [],
            userPreferences: {},
            selectedType: {
                id: "",
                type: "",
                label: ""
            },
            data: {
                allow_notification: false,
                show_banner: false,
                show_badge: false,
                all_provider: false
            },
            myRecordOnly: false,
            disableToggleBadge: false,
            disableToggleNotification: false
        };
    },
    computed: {
        userId() {
            return this.$route.params.id;
        },
        breadcrumbs() {
            return [
                {
                    text: "Settings",
                    href: process.env.APPS + "business/business"
                },
                {
                    text: "Profile",
                    to: {
                        name: "staff_view",
                        params: {
                            id: this.userId
                        }
                    }
                },
                {
                    text: "Notification Settings"
                }
            ];
        },
        displayProvider() {
            return (
                this.userPreferences[this.selectedType.type] &&
                this.userPreferences[this.selectedType.type].all_provider !=
                    null
            );
        },
        currentActor() {
            return this.$root.currentActor;
        },
        isProvider() {
            const currentActorId = this.$root.currentActor.id;
            const isProvider = this.providers.find(
                opt => opt.actor_id === currentActorId
            );

            return isProvider ? true : false;
        },
        displayUnassigned() {
            return (
                this.userPreferences[this.selectedType.type] &&
                this.userPreferences[this.selectedType.type].unassigned != null
            );
        }
    },
    mounted() {
        this.firstLoad();
    },
    methods: {
        async firstLoad() {
            this.isLoading = true;

            this.getType();
            await this.getProviders();
            await this.getUserPreferences();

            this.isLoading = false;
        },
        async getType() {
            const response = await NotificationService.getNotificationTypes();

            if (response.code == 200 && response.resources) {
                this.notificationTypes = response.resources;
                this.selectedType = response.resources[0];
            }
        },
        async getProviders() {
            const response = await StaffService.getProviders();

            if (response.resource?.data) {
                this.providers = response.resource.data;
            }
        },
        async getUserPreferences() {
            try {
                const response = await NotificationService.getUserPreferences(
                    this.userId
                );

                if (response.code == 200) {
                    const lastUpdatedDuration = response.resource.updated_at;
                    const json = response.resource.json;

                    if (lastUpdatedDuration) {
                        this.setIntervalAndTimeoutUpdatedDuration(
                            lastUpdatedDuration
                        );
                    }

                    let taskData = json.filter(val => val.category == "task");
                    let notiData = json.filter(
                        val => val.category == "notification"
                    );

                    if (taskData.length > 0) {
                        taskData = JSON.parse(`[${taskData[0].data[0]}]`);

                        this.notificationTypes.push({
                            type: "TASK",
                            label: "Task",
                            isactive: true
                        });

                        this.$set(this.userPreferences, "TASK", taskData[0]);
                    }

                    if (notiData.length > 0) {
                        notiData = JSON.parse(`[${notiData[0].data[0]}]`);

                        notiData.forEach(element => {
                            if (
                                (element.type == "DIAGNOSTIC_IMAGE" ||
                                    element.type == "LAB") &&
                                !element.unassigned
                            ) {
                                element.unassigned = false;
                            } else if (
                                element.type !== "DIAGNOSTIC_IMAGE" &&
                                element.type !== "LAB"
                            ) {
                                delete element.unassigned;
                            }
                            this.$set(
                                this.userPreferences,
                                element.type,
                                element
                            );
                        });
                    }

                    // check if notification type is missing from user preferences,
                    // if missing, initialize the object with all value set to false
                    this.notificationTypes.forEach(notificationTypes => {
                        if (!this.userPreferences[notificationTypes.type]) {
                            this.userPreferences[notificationTypes.type] = {
                                allow_notification: false,
                                show_badge: false,
                                show_banner: false,
                                type: notificationTypes.type

                                // if notification type allow provider selection
                                // To enable this part, perhaps BE can send response boolean param to state eiter the notification type has all_provider setting or not
                                // all_provider: true,
                                // provider_id: []
                            };
                        }
                    });

                    // update view based on user preference
                    this.updateData();
                }
            } catch (error) {
                // redirect to business setting page
                window.location.replace(
                    process.env.APPS + "index.php/business/business"
                );
            }
        },
        switchNotificationType(type) {
            this.selectedType = type;
            this.updateData();
        },
        validateAllowNotification() {
            if (this.data.show_badge && this.data.show_banner) {
                this.data.allow_notification = true;
            }
            // else if (!this.data.show_badge && !this.data.show_banner) {
            //     this.data.allow_notification = false;
            // }
        },
        updateBadgeBanner() {
            this.validateAllowNotification();
            this.save();
        },
        updateData() {
            const type = this.selectedType.type;
            const userPref = this.userPreferences[type];

            if (userPref) {
                this.data.show_badge = userPref.show_badge;
                this.data.show_banner = userPref.show_banner;
                this.data.allow_notification = userPref.allow_notification;
                this.data.unassigned = userPref.unassigned;
                this.validateAllowNotification();

                if (userPref.all_provider != null) {
                    let providerIds = [];

                    // in some cases, backend return provider_id as a string that contain only one provider id
                    if (typeof userPref.provider_id == "string") {
                        providerIds.push(userPref.provider_id);
                    } else if (typeof userPref.provider_id != "string") {
                        providerIds = userPref.provider_id;
                    }

                    // set by default to show multiple providers
                    this.myRecordOnly = false;

                    if (
                        providerIds.length == 1 &&
                        providerIds[0] == this.$root.currentActor.id
                    ) {
                        this.myRecordOnly = true;
                    }

                    this.data.all_provider = userPref.all_provider;

                    let providers = [];

                    // update providers array
                    // set by default all provider as false
                    this.providers.forEach(val => {
                        if (providerIds.find(e => e == val.actor_id)) {
                            val.isSelected = true;
                        } else {
                            val.isSelected = false;
                        }

                        providers.push(val);
                    });

                    this.providers = providers;
                }
            }

            if (type === "TASK") {
                this.data.allow_notification = true;
                this.data.show_badge = true;
                this.disableToggleBadge = true;
            } else {
                this.disableToggleBadge = false;
            }

            this.disableNotificationBadge(type);
        },
        updateIndividualProvider() {
            if (this.providers.every(val => val.isSelected == true)) {
                this.data.all_provider = true;
                this.save();
            } else {
                this.data.all_provider = false;
                this.save();
            }
        },
        resetProviderList() {
            // set all provider to false except currentActor
            this.providers.forEach(val => {
                if (val.actor_id == this.currentActor.id) {
                    val.isSelected = true;
                } else {
                    val.isSelected = false;
                }
            });
        },
        updateProviderToggle(type) {
            if (type == "own_record") {
                this.myRecordOnly = true;
                this.data.all_provider = false;
                this.resetProviderList();
                this.save();
            } else {
                this.resetProviderList();
                this.myRecordOnly = false;
                this.data.all_provider = false;
            }
        },
        updateAll() {
            if (!this.data.allow_notification) {
                this.data.show_badge = false;
                this.data.show_banner = false;
            } else {
                this.data.show_badge = true;
                this.data.show_banner = true;

                // select all provider by default when allow notification is changed to true
                this.myRecordOnly = false;
                this.data.all_provider = true;
                this.providers.forEach(val => (val.isSelected = true));
                this.data.unassigned = true;
            }

            if (this.selectedType.type === "TASK") {
                this.data.show_badge = true; // badge task always true
            }

            this.disableNotificationBadge(this.selectedType.type);

            this.save();
        },
        toggleAllProvider() {
            if (this.data.all_provider) {
                this.myRecordOnly = false;
                this.providers.forEach(val => (val.isSelected = true));
                this.save();
            } else {
                this.resetProviderList();
                this.save();
            }
        },
        async save() {
            this.isSaving = true;
            const type = this.selectedType.type;

            if (!this.userPreferences[type]) {
                this._message("Error : Unable to save changes", "danger");
                this.updateData();
                this.isSaving = false;
                return;
            }

            if (type === "TASK") {
                this.userPreferences[type].show_badge = this.data?.show_badge;
                this.userPreferences[type].show_banner = this.data?.show_banner;
                await this.saveTask();
                this.isSaving = false;
                return;
            }

            try {
                this.userPreferences[
                    type
                ].allow_notification = this.data?.allow_notification;
                this.userPreferences[type].show_badge = this.data?.show_badge;
                this.userPreferences[type].show_banner = this.data?.show_banner;

                if (this.userPreferences[type]?.all_provider != null) {
                    this.userPreferences[
                        type
                    ].all_provider = this.data.all_provider;

                    let providers = this.providers
                        .filter(e => e.isSelected)
                        .map(e => e.actor_id);

                    this.userPreferences[type].provider_id = providers;
                }
                if (type == "DIAGNOSTIC_IMAGE" || type == "LAB") {
                    this.userPreferences[
                        type
                    ].unassigned = this.data.unassigned;
                } else {
                    delete this.userPreferences[type].unassigned;
                }

                let param = {
                    category: "notification",
                    json: ""
                };

                let idx = 0;

                for (const key in this.userPreferences) {
                    if (key != "TASK") {
                        param.json =
                            idx == 0
                                ? JSON.stringify(this.userPreferences[key])
                                : `${param.json},${JSON.stringify(
                                      this.userPreferences[key]
                                  )}`;
                        idx++;
                    }
                }

                await NotificationService.saveUserPreferences(
                    this.userId,
                    param
                );

                this.setIntervalAndTimeoutUpdatedDuration();
                this.updateStore(param.json);
            } catch (error) {
                this._message("Error : Unable to save changes", "danger");
            } finally {
                this.isSaving = false;
            }
        },
        async saveTask() {
            await NotificationService.saveUserPreferences(this.userId, {
                category: "task",
                json: JSON.stringify({
                    show_banner: this.data?.show_banner,
                    show_badge: true // always true
                })
            });
        },
        async updateStore(json) {
            // trigger update across tab with socket
            this.$socket.client.emit("v-broadcast-all", {
                action: "user-prefs-notification-update",
                businessId: this.$root.setting.business_id,
                userId: this.$root.currentActor.id,
                json: json
            });
        },
        disableIndividual(provider) {
            const isOneProviderSelected =
                this.providers.filter(element => element.isSelected).length ===
                1;

            return (
                provider.isSelected == true &&
                isOneProviderSelected &&
                this.isProvider
            );
        },
        disableNotificationBadge(type) {
            if (type === "REPORT" && this.data.allow_notification) {
                this.disableToggleNotification = true;
            } else {
                this.disableToggleNotification = false;
            }
        }
    }
};
</script>

<style>
.banner-tooltip > .tooltip-inner {
    max-width: 400px !important;
}
</style>
